import React, { useEffect, useRef, useState } from "react";
import { Camera, X, RotateCcw, Check, Loader2, ScanLine, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";

// Full-screen camera modal with rectangular ID-card overlay guide.
// onCapture(blob) is called with a JPEG Blob ready to upload.
// onClose() to dismiss without capturing.
export default function CameraCapture({ open, onClose, onCapture, hint = "Allinea il documento dentro la cornice" }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setError("");
    setPreviewUrl(null);
    setPreviewBlob(null);
    setReady(false);

    (async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          setError("La fotocamera non è disponibile su questo browser. Usa 'Carica file'.");
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (!mounted) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
          setReady(true);
        }
      } catch (e) {
        const msg = e?.name === "NotAllowedError"
          ? "Permesso fotocamera negato. Abilitalo dalle impostazioni del browser."
          : e?.name === "NotFoundError"
          ? "Nessuna fotocamera disponibile sul dispositivo."
          : "Impossibile aprire la fotocamera. Usa 'Carica file'.";
        setError(msg);
      }
    })();

    return () => {
      mounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const closeAll = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewBlob(null);
    setReady(false);
    onClose?.();
  };

  const takePhoto = async () => {
    if (!videoRef.current || !ready) return;
    setBusy(true);
    try {
      const video = videoRef.current;
      const w = video.videoWidth;
      const h = video.videoHeight;
      const canvas = canvasRef.current || document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            setPreviewBlob(blob);
            setPreviewUrl(URL.createObjectURL(blob));
          }
          setBusy(false);
        },
        "image/jpeg",
        0.92,
      );
    } catch (e) {
      setBusy(false);
    }
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewBlob(null);
  };

  const confirm = () => {
    if (!previewBlob) return;
    const file = new File([previewBlob], `doc-${Date.now()}.jpg`, { type: "image/jpeg" });
    onCapture?.(file);
    closeAll();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-black flex flex-col" data-testid="camera-modal">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <button onClick={closeAll} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Chiudi" data-testid="camera-close">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 text-sm font-bold">
          <ScanLine className="w-4 h-4 text-sky-400" />
          {previewBlob ? "Anteprima documento" : "Fotocamera"}
        </div>
        <div className="w-10" />
      </div>

      {/* Main viewport */}
      <div className="relative flex-1 overflow-hidden bg-black">
        {error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-white">
            <AlertTriangle className="w-10 h-10 text-amber-400 mb-3" />
            <div className="font-bold mb-2">Fotocamera non disponibile</div>
            <p className="text-sm text-slate-300 max-w-xs">{error}</p>
            <Button onClick={closeAll} variant="outline" className="mt-5 rounded-full">Chiudi</Button>
          </div>
        ) : previewUrl ? (
          <img src={previewUrl} alt="anteprima" className="w-full h-full object-contain" />
        ) : (
          <>
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              className="w-full h-full object-cover"
              data-testid="camera-video"
            />
            <canvas ref={canvasRef} hidden />

            {/* Overlay guide */}
            <div className="absolute inset-0 pointer-events-none">
              {/* Darken outside */}
              <div className="absolute inset-0">
                <div className="absolute inset-x-0 top-0 h-[15%] bg-black/55" />
                <div className="absolute inset-x-0 bottom-0 h-[15%] bg-black/55" />
                <div className="absolute left-0 top-[15%] bottom-[15%] w-[6%] bg-black/55" />
                <div className="absolute right-0 top-[15%] bottom-[15%] w-[6%] bg-black/55" />
              </div>
              {/* Frame */}
              <div className="absolute left-[6%] right-[6%] top-[15%] bottom-[15%] rounded-2xl ring-2 ring-sky-400/80">
                <Corner cls="top-[-2px] left-[-2px] border-t-4 border-l-4 rounded-tl-2xl" />
                <Corner cls="top-[-2px] right-[-2px] border-t-4 border-r-4 rounded-tr-2xl" />
                <Corner cls="bottom-[-2px] left-[-2px] border-b-4 border-l-4 rounded-bl-2xl" />
                <Corner cls="bottom-[-2px] right-[-2px] border-b-4 border-r-4 rounded-br-2xl" />
              </div>
              {/* Hint */}
              <div className="absolute top-[6%] left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-md text-white text-xs font-semibold px-4 py-2 rounded-full">
                {hint}
              </div>
            </div>

            {!ready && (
              <div className="absolute inset-0 flex items-center justify-center text-white">
                <Loader2 className="w-10 h-10 animate-spin" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom controls */}
      <div className="bg-black/80 backdrop-blur-md p-5 flex items-center justify-center gap-6">
        {previewBlob ? (
          <>
            <Button
              onClick={retake}
              variant="outline"
              className="rounded-full bg-white/5 border-white/30 text-white hover:bg-white/10 h-12 px-5"
              data-testid="camera-retake"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Rifai
            </Button>
            <Button
              onClick={confirm}
              className="rounded-full bg-sky-500 hover:bg-sky-600 text-white h-12 px-6 font-bold"
              data-testid="camera-confirm"
            >
              <Check className="w-5 h-5 mr-2" /> Usa questa foto
            </Button>
          </>
        ) : (
          <button
            onClick={takePhoto}
            disabled={!ready || busy || !!error}
            aria-label="Scatta foto"
            className="w-20 h-20 rounded-full bg-white text-slate-900 ring-4 ring-white/30 flex items-center justify-center disabled:opacity-50 active:scale-95 transition-transform"
            data-testid="camera-shoot"
          >
            {busy ? <Loader2 className="w-7 h-7 animate-spin" /> : <Camera className="w-8 h-8" />}
          </button>
        )}
      </div>
    </div>
  );
}

function Corner({ cls }) {
  return <div className={`absolute w-8 h-8 border-sky-400 ${cls}`} />;
}
